import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { formatUSD } from "@/lib/marketplaceTerms";
import { fmtDate } from "@/components/PaymentProgressCard";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", tries: 0, data: null, error: "" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", tries: 0, data: null, error: "Missing session_id" }); return; }
    let cancelled = false;
    const MAX = 20;      // ~40s at 2s interval
    const poll = async (tries) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", tries, data, error: "" });
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setState({ status: "expired", tries, data, error: "" });
          return;
        }
        if (tries >= MAX) {
          setState({ status: "timeout", tries, data, error: "Still processing — check back in a minute" });
          return;
        }
        setState({ status: "polling", tries, data, error: "" });
        setTimeout(() => poll(tries + 1), 2000);
      } catch (e) {
        setState({ status: "error", tries, data: null, error: e.response?.data?.detail || "Failed to fetch payment status" });
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-24 text-center">
      {state.status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[color:var(--accent)] animate-spin" />
          <h1 className="font-serif text-4xl mt-6">Finalising your payment…</h1>
          <p className="text-[color:var(--text-muted)] mt-3">Hold tight, this usually takes a few seconds.</p>
        </>
      )}
      {state.status === "paid" && (() => {
        const isBalance = String(state.data?.kind || "").toLowerCase().includes("balance");
        const prog = state.data?.booking_progress;
        const fullyPaid = !!prog?.fully_paid;
        const due = fmtDate(prog?.due_date);
        return (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#8A9A86]" data-testid="paid-icon" />
          <h1 className="font-serif text-4xl mt-6" data-testid="paid-heading">
            {fullyPaid ? "Paid in full" : isBalance ? "Payment received" : "Deposit received"}
          </h1>
          <p className="text-[color:var(--text-muted)] mt-3" data-testid="paid-amount">
            ${state.data?.amount?.toFixed(2)} paid — {fullyPaid ? "your booking is now paid in full and the vendor has been notified." : "the vendor has been notified."}
          </p>
          {prog && !fullyPaid && prog.remaining > 0 && (
            <p className="mt-4 text-lg font-semibold" data-testid="paid-progress-line">
              {formatUSD(prog.paid)} paid · {formatUSD(prog.remaining)} remaining{due ? ` · due by ${due}` : ""}
            </p>
          )}
          {state.data?.tip > 0 && (
            <p className="text-xs text-[color:var(--text-muted)] mt-2" data-testid="paid-tip">
              Including ${state.data.tip.toFixed(2)} tip · 100% goes to the vendor
            </p>
          )}
          <div className="mt-8 flex gap-3 justify-center">
            {state.data?.booking_id && (
              <Button data-testid="view-booking-btn" onClick={() => nav(`/bookings/${state.data.booking_id}`)} className="btn-sage rounded-full h-11 px-6">
                View booking
              </Button>
            )}
            <Link to="/planner/dashboard"><Button variant="outline" className="rounded-full h-11 px-6">Back to dashboard</Button></Link>
          </div>
        </>
        );
      })()}
      {state.status === "timeout" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[color:var(--text-muted)]" />
          <h1 className="font-serif text-4xl mt-6">Still processing</h1>
          <p className="text-[color:var(--text-muted)] mt-3">This can take a moment. Refresh in a bit or check your dashboard.</p>
        </>
      )}
      {state.status === "expired" && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-[color:var(--accent)]" />
          <h1 className="font-serif text-4xl mt-6">Checkout expired</h1>
          <p className="text-[color:var(--text-muted)] mt-3">Please start a new deposit from your booking page.</p>
        </>
      )}
      {state.status === "error" && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-[color:var(--accent)]" />
          <h1 className="font-serif text-4xl mt-6">Something went wrong</h1>
          <p className="text-[color:var(--text-muted)] mt-3">{state.error}</p>
        </>
      )}
    </div>
  );
}
